import { useEffect, useRef, useCallback, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { transitions, durations, reducedMotionCSS } from '../../utils/motion';

type ModalSize = 'sm' | 'md' | 'lg' | 'fullscreen';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  className?: string;
  closeOnOverlay?: boolean;
}

const sizeStyles: Record<ModalSize, { maxWidth: string; borderRadius: string }> = {
  sm: { maxWidth: '400px', borderRadius: '10px' },
  md: { maxWidth: '560px', borderRadius: '12px' },
  lg: { maxWidth: '720px', borderRadius: '12px' },
  fullscreen: { maxWidth: 'calc(100vw - 2rem)', borderRadius: '0' },
};

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  className,
  closeOnOverlay = true,
}: ModalProps): JSX.Element | null {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const titleId = useRef(`ds-modal-title-${Math.random().toString(36).slice(2, 9)}`);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      // Focus trap
      if (e.key === 'Tab' && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (!firstElement || !lastElement) return;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement;
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';

      // Focus first focusable element on next tick
      const raf = requestAnimationFrame(() => {
        const firstFocusable = modalRef.current?.querySelector<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        firstFocusable?.focus();
      });

      return () => {
        cancelAnimationFrame(raf);
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
        previousActiveElement.current?.focus();
      };
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const sizeStyle = sizeStyles[size];

  return (
    <div
      className="ds-modal__overlay"
      onClick={(e) => {
        if (closeOnOverlay && e.target === e.currentTarget) onClose();
      }}
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 10000,
        animation: `ds-modal-fade-in ${durations.fade} ease`,
      }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId.current}
        className={cn('ds-modal', `ds-modal--${size}`, className)}
        style={{
          width: '100%',
          maxWidth: sizeStyle.maxWidth,
          /* O overlay tem `padding: 1rem`. Com `100vh` de altura, o dialogo
             fullscreen ficava 2rem maior que o espaco disponivel e, por estar
             centralizado, perdia o cabecalho para fora da tela — o titulo
             aparecia cortado ao meio. A largura ja descontava o padding
             (`calc(100vw - 2rem)`); a altura nao. */
          maxHeight: size === 'fullscreen' ? 'calc(100vh - 2rem)' : '85vh',
          height: size === 'fullscreen' ? 'calc(100vh - 2rem)' : undefined,
          background: '#ffffff',
          borderRadius: sizeStyle.borderRadius,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
          animation: `ds-modal-scale-in ${durations.fade} ease`,
          outline: 'none',
        }}
      >
        {/* Header */}
        <div
          className="ds-modal__header"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1rem 1.5rem',
            borderBottom: '1px solid #c4c6cf',
            flexShrink: 0,
          }}
        >
          <h2
            id={titleId.current}
            style={{
              margin: 0,
              fontSize: '1rem',
              fontWeight: 600,
              color: '#191c1e',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            aria-label="Fechar"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1.25rem',
              color: '#43474e',
              padding: '0.25rem',
              borderRadius: '4px',
              lineHeight: 1,
              flexShrink: 0,
              transition: `background ${transitions.fast}`,
            }}
            className="ds-modal__close"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div
          className="ds-modal__body"
          style={{
            padding: '1.5rem',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div
            className="ds-modal__footer"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '0.75rem',
              padding: '1rem 1.5rem',
              borderTop: '1px solid #c4c6cf',
              flexShrink: 0,
            }}
          >
            {footer}
          </div>
        )}
      </div>

      <style>{`
        .ds-modal__close:hover {
          background: #eceef0;
          color: #191c1e;
        }
        ${reducedMotionCSS('.ds-modal__overlay', '.ds-modal')}
      `}</style>
    </div>
  );
}

Modal.displayName = 'Modal';

